import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import WorkspaceOverviewPreview from "./previewTabs/WorkspaceOverviewPreview";
import QualityOverviewPreview from "./previewTabs/QualityOverviewPreview";
import ComplianceAIToolsPreview from "./previewTabs/ComplianceAIToolsPreview";
import ImplementationTrackerPreview from "./previewTabs/ImplementationTrackerPreview";
import DemoNavigatorTour from "./DemoNavigatorTour";
import "./demo.css";

const TABS = [
  { id: "workspace", label: "Workspace", icon: LayoutDashboard, navId: "demo-tab-workspace" },
  { id: "quality", label: "Quality Overview", icon: ShieldCheck, navId: "demo-tab-quality" },
  { id: "ai-tools", label: "Compliance AI Tools", icon: Sparkles, navId: "demo-tab-ai-tools" },
  { id: "implementation", label: "Implementation", icon: TrendingUp, navId: "demo-tab-implementation" },
];

/**
 * Public, unauthenticated preview of the Client Portal. Built as a SEPARATE
 * static component tree (not a reuse of the real portal components), because
 * every real portal component fetches live, project-scoped entity data via
 * base44.entities.*.filter({ project_id }) and several pages in this app
 * (e.g. QualityPlatform.jsx) additionally gate on base44.auth.me() even where
 * there's no route-level guard. None of that is safe or even functional for
 * an anonymous visitor, so this shell renders only from portalPreviewData.js
 * — no entity reads, no auth calls, nothing that could leak real client data.
 */
export default function PortalPreviewShell() {
  const [activeTab, setActiveTab] = useState("workspace");

  return (
    <div className="min-h-screen bg-background">
      <header className="lcx-header">
        <div className="lcx-header-inner">
          <Link
            to="/"
            aria-label="Back to LabComplyx"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.8rem", fontWeight: 600, textDecoration: "none", color: "inherit", opacity: 0.8, marginRight: "0.75rem" }}
          >
            ← labcomplyx.com
          </Link>
          <div className="lcx-brand">
            <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
              <rect width="30" height="30" rx="7" fill="#161b27" stroke="rgba(255,255,255,0.14)" />
              <polyline points="5,21 11,15 16,18 25,8" fill="none" stroke="#00d4aa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="11" cy="15" r="2" fill="#00d4aa" />
              <circle cx="16" cy="18" r="2" fill="#4f8fff" />
              <circle cx="25" cy="8" r="2" fill="#a78bfa" />
            </svg>
            <span>LabComplyx</span>
            <span className="lcx-live-badge">Portal Preview</span>
          </div>
          <div className="lcx-header-actions">
            <Link to="/gap-assessment" className="lcx-cta">Request Full Access →</Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <p className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2 inline-block">
          You're viewing a preview with sample data. Nothing here is a real client's information.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="flex flex-wrap gap-2 border-b border-border pb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-navigator-id={t.navId}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-lg transition-colors ${
                  active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {activeTab === "workspace" && <WorkspaceOverviewPreview />}
        {activeTab === "quality" && <QualityOverviewPreview />}
        {activeTab === "ai-tools" && <ComplianceAIToolsPreview />}
        {activeTab === "implementation" && <ImplementationTrackerPreview />}
      </main>

      <DemoNavigatorTour activeTab={activeTab} onNavigate={setActiveTab} />
    </div>
  );
}
